<template>
  <div class="email-modal-container">
    <transition name="expand">
      <div v-if="open" class="email-alert">
        <span>When:</span>
        <span
          ref="excludeButton"
          class="close-btn"
          data-tool-tip="Close"
          @click="open = false"
          @click.stop
        >
          <font-awesome-icon :icon="['far', 'times']" class="check-on checkbox-active" />
        </span>
        <div class="schedule-when">
          <b-select
            class="frequency-select"
            :options="frequencyOptions"
            :selected="frequency"
            @change="onFrequencyChange"
          />
        </div>
        <div class="date-container">
          <div class="date-field">
            <span>Start Date:</span>
            <div class="input-icon-wrapper">
              <input
                ref="startDateInput"
                v-model="scheduleStartDate"
                type="date"
                class="date-input"
                :min="currentDate"
                :max="maxDate"
                @change="handleStartDateChange"
                @blur="validateStartDate"
                @focus="setCurrentValueStartDate"
              />
              <font-awesome-icon
                :icon="['far', 'fa-calendar-alt']"
                class="calendar-icon"
                @click="$refs.startDateInput.showPicker()"
              />
            </div>
          </div>
          <div class="date-field">
            <span>End Date:</span>
            <div class="input-icon-wrapper">
              <input
                ref="endDateInput"
                v-model="scheduleEndDate"
                type="date"
                class="date-input"
                :min="currentDate"
                :max="maxDate"
                @change="handleEndDateChange"
                @blur="validateEndDate"
                @focus="setCurrentValueEndDate"
              />
              <font-awesome-icon
                :icon="['far', 'fa-calendar-alt']"
                class="calendar-icon"
                @click="$refs.endDateInput.showPicker()"
              />
            </div>
          </div>
        </div>
        <span>Email To:</span>
        <div class="email-container" @click="focusInput">
          <div v-for="(email, index) in localEmails" :key="index" class="email-chip">
            {{ email }}
            <span class="remove" @click="removeEmail(index)">&times;</span>
          </div>
          <textarea
            ref="textareasched"
            v-model="input"
            placeholder="Type emails..."
            class="email-input"
            rows="1"
            @keydown.enter.prevent="addEmail"
            @keydown.space.prevent="addEmail"
            @keydown.backspace="handleBackspace"
            @keydown="handleKeydown"
            @input="adjustHeight"
          ></textarea>
        </div>
        <div v-if="errorMsg" class="error">{{ errorMsg }}</div>
        <div style="padding-top: 10px;">
          <k-button :size="4" label="Save" :disabled="!!errorMsg" @click="saveReportScheduleInfo" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { SCHEDULE_FREQUENCY_OPTIONS } from '@/constant';
import BSelect from '@/components/elements/b-select.vue';

export default {
  components: {
    BSelect,
  },
  props: {
    emails: {
      required: false,
      type: Array,
      default: () => [],
    },
    scheduleItem: {
      required: false,
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      open: false,
      input: '',
      isListenerAdded: false,
      errorMsg: null,
      frequencyOptions: SCHEDULE_FREQUENCY_OPTIONS,
      frequency: 'select',
      scheduleStartDate: '',
      currentValStartDate: '',
      scheduleEndDate: '',
      currentValEndDate: '',
      currentDate: this.getTomorrowDate(), // Set min date to tomorrow
      maxDate: this.formatDate(this.addYears(new Date(), 1)), // Max date (1 year from today)
      localEmails: [], // Local copy for editing
    };
  },
  watch: {
    emails(newVal) {
      if (!this.open) {
        this.localEmails = [...newVal];
      }
    },
    open(n) {
      if (n) {
        this.input = '';
        this.errorMsg = null;
        this.localEmails = Array.isArray(this.emails) ? [...this.emails] : [];
        if (!this.isListenerAdded) {
          document.addEventListener('click', this.handleClickOutside);
          this.isListenerAdded = true;
        }
      } else if (this.isListenerAdded) {
        document.removeEventListener('click', this.handleClickOutside);
        this.isListenerAdded = false;
      }
    },
    scheduleItem: {
      immediate: true,
      handler(newVal) {
        this.frequency = newVal && newVal.schedule ? newVal.schedule : 'select';
        this.scheduleStartDate =
          newVal && newVal.scheduleStartDate ? newVal.scheduleStartDate : this.currentDate;
        this.scheduleEndDate =
          newVal && newVal.scheduleEndDate ? newVal.scheduleEndDate : this.currentDate;
      },
    },
  },
  created() {
    this.frequency =
      this.scheduleItem && this.scheduleItem.schedule ? this.scheduleItem.schedule : 'select';
    this.scheduleStartDate =
      this.scheduleItem && this.scheduleItem.scheduleStartDate
        ? this.scheduleItem.scheduleStartDate
        : this.currentDate;
    this.scheduleEndDate =
      this.scheduleItem && this.scheduleItem.scheduleEndDate
        ? this.scheduleItem.scheduleEndDate
        : this.currentDate;
  },
  mounted() {
    window.addEventListener('click', this.onClickOutside, true);
  },
  beforeDestroy() {
    window.removeEventListener('click', this.onClickOutside, true);
  },
  methods: {
    getTomorrowDate() {
      const today = new Date().toISOString().split('T')[0];
      const todayDateObj = new Date(today);
      const tomorrowDateObj = new Date(todayDateObj);
      tomorrowDateObj.setDate(todayDateObj.getDate() + 1);
      return tomorrowDateObj.toISOString().split('T')[0];
    },
    openDatePicker(refName) {
      const input = this.$refs[refName];
      if (input) {
        input.focus();
        setTimeout(() => input.click(), 50);
      }
    },
    formatDate(date) {
      return date.toISOString().split('T')[0];
    },
    addYears(date, years) {
      const newDate = new Date(date);
      newDate.setFullYear(newDate.getFullYear() + years);
      return newDate;
    },
    onFrequencyChange(val) {
      this.frequency = val;
    },
    handleEndDateChange(event) {
      this.scheduleEndDate = event.target.value;
      if (this.scheduleStartDate && this.scheduleEndDate < this.scheduleStartDate) {
        this.scheduleStartDate = this.scheduleEndDate;
      }
    },
    validateEndDate() {
      if (this.currentValEndDate === this.scheduleEndDate) {
        return;
      }
      if (this.scheduleEndDate < this.currentDate) {
        this.scheduleEndDate = this.currentDate;
      }
      if (this.scheduleStartDate && this.scheduleEndDate < this.scheduleStartDate) {
        this.scheduleStartDate = this.scheduleEndDate;
      }
    },
    setCurrentValueEndDate() {
      this.currentValEndDate = this.scheduleEndDate;
    },
    handleStartDateChange(event) {
      this.scheduleStartDate = event.target.value;
      if (this.scheduleEndDate && this.scheduleEndDate < this.scheduleStartDate) {
        this.scheduleEndDate = this.scheduleStartDate;
      }
    },
    validateStartDate() {
      if (this.currentValStartDate === this.scheduleStartDate) {
        return;
      }
      if (this.scheduleStartDate < this.currentDate) {
        this.scheduleStartDate = this.currentDate;
      }
      if (this.scheduleEndDate && this.scheduleEndDate < this.scheduleStartDate) {
        this.scheduleEndDate = this.scheduleStartDate;
      }
    },
    setCurrentValueStartDate() {
      this.currentValStartDate = this.scheduleStartDate;
    },
    onClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.open = false;
      }
    },
    handleClickOutside(event) {
      const textarea = this.$refs.textareasched;
      const button = this.$refs.excludeButton;
      if (!textarea?.contains(event.target) && !button?.contains(event.target)) {
        this.addEmail();
      }
    },
    saveReportScheduleInfo() {
      this.addEmail();
      if (this.localEmails.length === 0) {
        this.errorMsg = 'Please enter at least one valid email id';
        return;
      }
      if (!this.localEmails.every(this.validateEmail)) {
        this.errorMsg = 'Please enter valid email id';
        return;
      }
      this.$emit(
        'save-report-schedule-info',
        this.localEmails,
        this.frequency,
        this.scheduleStartDate,
        this.scheduleEndDate
      );
    },
    openClosePopup() {
      this.open = !this.open;
    },
    focusInput() {
      this.$refs.textareasched.focus();
    },
    removeEmail(index) {
      this.localEmails.splice(index, 1);
      this.adjustHeightSched();
      this.$emit('emails-changed', this.localEmails);
    },
    adjustHeightSched() {
      this.$nextTick(() => {
        this.$refs.textareasched.style.height = 'auto';
        this.$refs.textareasched.style.height = `${this.$refs.textareasched.scrollHeight}px`;
      });
    },
    addEmail() {
      const trimmed = this.input.trim();
      if (!trimmed || this.localEmails.includes(trimmed)) {
        this.input = '';
        return;
      }
      if (this.validateEmail(trimmed)) {
        this.localEmails.push(trimmed);
        this.input = '';
        this.adjustHeight();
        this.$emit('emails-changed', this.localEmails);
        this.errorMsg = null;
      } else {
        this.errorMsg = 'Please enter valid email id';
      }
    },
    validateEmail(email) {
      return /\S+@\S+\.\S+/.test(email);
    },
    handleBackspace() {
      if (this.input === '' && this.localEmails.length) {
        this.localEmails.pop();
        this.adjustHeightSched();
      }
    },
    handleKeydown(event) {
      if (event.key === ',') {
        event.preventDefault();
        this.addEmail();
        this.adjustHeightSched();
      }
    },
    adjustHeight(event) {
      const textarea = event ? event.target : this.$refs.textarea;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
      if (
        this.localEmails.every(this.validateEmail) &&
        (!this.input || this.validateEmail(this.input.trim()))
      ) {
        this.errorMsg = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.email-modal-container {
  z-index: 10;
  margin: 0px;
}
.errorBox {
  border: 1px solid #fc4242 !important;
}
.error {
  padding: 10px;
  font-size: 13px;
  color: rgba(252, 66, 66);
  letter-spacing: 0.9px;
}
.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.close-btn {
  float: right;
  padding-bottom: 10px;
}
.ex-dl-btn {
  color: #303335;
}
.expand-enter-active,
.expand-leave-active {
  -webkit-transition: top 0.3s ease, opacity 0.3s ease;
  -moz-transition: top 0.3s ease, opacity 0.3s ease;
  -ms-transition: top 0.3s ease, opacity 0.3s ease;
  -o-transition: top 0.3s ease, opacity 0.3s ease;
  transition: top 0.3s ease, opacity 0.3s ease;
}

.expand-enter,
.expand-leave-to {
  top: 100% !important;
  opacity: 0 !important;
}

.progress100 {
  width: 100% !important;
}

svg:not(:root).svg-inline--fa {
  overflow: visible;
}
.svg-inline--fa {
  display: inline-block;
  height: 1em;
  overflow: visible;
  font-size: inherit;
  vertical-align: -0.125em;
}
.svg-inline--fa.fa-w-12 {
  width: 0.75em;
}

@media screen {
  * {
    box-sizing: border-box;
  }
  * {
    outline: 0;
  }
  .email-alert {
    position: absolute;
    top: 21%;
    left: 78.9%;
    width: 300px;
    padding: 16px 30px;
    color: #fff;
    // text-align: center;
    background-color: rgba(29, 33, 37, 0.95);
    border-radius: 5px;
    transform: translateX(-20%);
    z-index: 10;

    .success {
      font-size: 13px;
      color: rgba(107, 207, 24);
    }
    .fail {
      font-size: 13px;
      color: rgba(252, 66, 66);
    }

    input[type='email'] {
      width: 230px;
      margin: 0.5rem 0;
      font-size: 14px;
      line-height: 33px;
      border-radius: 0.15rem;
    }
  }
  .email-alert-on-saved-rep {
    position: absolute;
    top: 21%;
    left: 29%;
    width: 300px;
    padding: 16px 30px;
    color: #fff;
    // text-align: center;
    background-color: rgba(29, 33, 37, 0.95);
    border-radius: 5px;
    transform: translateX(-20%);
    z-index: 10;

    .success {
      font-size: 13px;
      color: rgba(107, 207, 24);
    }
    .fail {
      font-size: 13px;
      color: rgba(252, 66, 66);
    }

    input[type='email'] {
      width: 230px;
      margin: 0.5rem 0;
      font-size: 14px;
      line-height: 33px;
      border-radius: 0.15rem;
    }
  }
  .flp-btn {
    position: relative;
    display: inline-block;
    padding: 15px 26px;
    font-size: 12px;
    font-weight: 400;
    color: var(--buttontext) !important;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    cursor: pointer;
    user-select: none;
    background-color: var(--primarycolor) !important;
    -webkit-border-radius: 0;
    border-radius: 3px;
    transition: all 0.6s ease;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    -webkit-touch-callout: none;
  }
  .btn-size2 {
    min-width: 20px;
    padding: 11px;
    padding-left: 17px;
    font-size: 12px;
    font-weight: 600;
    &:hover {
      color: #fff !important;
      background-color: #424242 !important;
      border: none !important;
    }
  }
  .flp-btn-icon-size2 {
    position: relative;
    top: 2px;
    padding: 5px 4px 4px 12px;
    margin: 0 0 0 13px;
    font-size: 18px;
    border-left: 1px solid rgba(0, 0, 0, 0.15);
  }
  .hide {
    display: none !important;
  }
  ::-webkit-scrollbar {
    width: 5px;
    height: 7px;
  }
  ::-webkit-scrollbar-button {
    width: 0;
    height: 0;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(111, 111, 111, 0.2);
    border: 0 #fff;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #525965;
  }
  ::-webkit-scrollbar-thumb:active {
    background: #525965;
  }
  ::-webkit-scrollbar-track {
    background: 0 0;
    border: 0 #fff;
    border-radius: 50px;
  }
  ::-webkit-scrollbar-track:hover {
    background: 0 0;
  }
  ::-webkit-scrollbar-track:active {
    background: 0 0;
  }
  ::-webkit-scrollbar-corner {
    background: 0 0;
  }
}

.fa-arrow-to-bottom:before {
  content: '\f33d';
}

.alt-loader {
  position: relative;
  display: block;
  width: 100%;
  height: 4px;
  margin: 11px 0px 2px 0px;
}
.alt-loader-bg {
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.1);
}
.alt-loader-progress {
  position: absolute;
  z-index: 3;
  width: 1%;
  height: 4px;
  background-color: #00abb7;
  -webkit-transition: width 1s ease;
  -moz-transition: width 1s ease;
  -ms-transition: width 1s ease;
  -o-transition: width 1s ease;
  transition: width 1s ease;
}
.fp-text-link {
  font-size: 12px;
  color: #707e8a;
  display: inline-block;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  & p {
    text-decoration: underline;
    cursor: pointer;
  }
  & p:hover {
    color: var(--primarycolor) !important;
  }
  & span {
    text-decoration: none;
    margin-left: 8px;
    color: #212325 !important;
    .advance-filter-clear-icon:hover {
      color: var(--primarycolor) !important;
    }
  }
  & span:hover {
    color: #cad1d6;
  }
}
.custom-btn {
  border-radius: 6px !important;
  padding: 7px 12px 8px !important;
}
.txt-center {
  text-align: center;
  z-index: 99 !important;
  max-width: 20px !important;
  padding-right: 100px !important;
  padding-bottom: 50px !important;
  max-height: 10px !important;
}

.email-container {
  display: flex;
  flex-wrap: wrap;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: text;
  min-height: 40px;
  width: 250px;
}

.email-chip {
  align-items: center;
  background-color: var(--primarycolor);
  padding: 5px 30px 5px 12px;
  border-radius: 20px;
  margin: 3px;
  font-size: 14px;
  color: var(--buttontext);
  word-wrap: break-word;
  max-width: calc(100% - 20px); /* Reserve space for remove button */
  white-space: normal;
  overflow: hidden;
  flex-shrink: 0;
  position: relative; /* Add this to position the remove button */
  text-overflow: ellipsis; /* Display ellipsis when text overflows */
}

.remove {
  margin-left: 5px;
  cursor: pointer;
  font-weight: bold;
  position: absolute; /* Absolute position */
  right: 12px; /* Position it to the right */
  top: 50%;
  transform: translateY(-50%); /* Center it vertically */
}

.email-input {
  flex-grow: 1;
  border: none;
  outline: none;
  font-size: 14px;
  min-width: 100px;
  resize: none;
  overflow: hidden;
  padding-top: 6px;
}

.schedule-when {
  padding-bottom: 10px;
}

::v-deep .schedule-when .dropdown.noselect.frequency-select {
  min-width: 250px !important;
}

.date-picker-wrapper {
  position: relative;
  display: inline-block;
  padding-bottom: 10px;
}

.date-input {
  font-family: inherit;
  font-size: 14px;
  color: #000;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid var(--primarycolor); /* Underline style */
  padding: 4px 10px 4px 0; /* Adjust padding for icon space */
  cursor: pointer;
  outline: none;
  width: 120px;
}

.date-input::-webkit-calendar-picker-indicator {
  opacity: 0; /* Hide default date picker icon */
}

.calendar-icon {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--primarycolor); /* Match UI theme */
  cursor: pointer;
  font-size: 16px;
  pointer-events: auto; /* Prevent interference */
}

.date-input:focus {
  border-bottom: 2px solid var(--primarycolor); /* Highlighted color */
}

.date-container {
  display: flex;
  gap: 10px; /* Space between fields */
  align-items: center;
  padding-bottom: 10px;
}

.date-field {
  display: flex;
  flex-direction: column;
}

.date-input.small {
  width: 120px; /* Reduce width */
}

.input-icon-wrapper {
  position: relative;
  width: fit-content;
}

.error-highlight {
  border: 2px solid var(--primaryred);
  border-radius: 5px;
  transition: border-color 0.3s ease;
  padding: 2px 2px;
}

.checkbox-line {
  display: flex;
  align-items: center;
}
</style>
